import React, { useEffect, useState } from 'react';
import {
  Box, Paper, Typography, CircularProgress, Alert, Button, Chip, Accordion,
  AccordionSummary, AccordionDetails, Table, TableBody, TableCell, TableHead,
  TableRow, TextField, IconButton, Tooltip, alpha, useTheme
} from '@mui/material';
import {
  CheckCircle as PassIcon,
  Error as CriticalIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  Assignment as ReportIcon,
} from '@mui/icons-material';
import hrAuditService, { HrAuditReport, HrAuditCheck } from '../services/hrAudit.service';
import { useLanguage } from '../contexts/LanguageContext';

const HrAuditReportPage: React.FC = () => {
  const { language } = useLanguage();
  const isRTL = language === 'ar';
  const theme = useTheme();

  const today = new Date();
  const defaultFrom = new Date(today.getFullYear(), today.getMonth() - 1, 21);
  const defaultTo = new Date(today.getFullYear(), today.getMonth(), 20);
  const toLocalISO = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

  const [fromDate, setFromDate] = useState(toLocalISO(defaultFrom));
  const [toDate, setToDate] = useState(toLocalISO(defaultTo));
  const [report, setReport] = useState<HrAuditReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const t = {
    title: isRTL ? 'تقرير الرقابة والتدقيق على الموارد البشرية' : 'HR Integrity Audit Report',
    subtitle: isRTL
      ? 'يفحص الموظفين، التايم شيت، الإجازات، الرواتب، القيود المحاسبية والعهد — يكشف أي خلل قبل الترحيل'
      : 'Audits employees, timesheets, leaves, payrolls, journal entries, and custodies — detects issues before posting',
    from: isRTL ? 'من تاريخ' : 'From',
    to: isRTL ? 'إلى تاريخ' : 'To',
    runAudit: isRTL ? 'تشغيل الفحص' : 'Run Audit',
    running: isRTL ? 'جارٍ الفحص...' : 'Auditing...',
    period: isRTL ? 'الفترة' : 'Period',
    generated: isRTL ? 'تم الإنشاء' : 'Generated',
    summary: isRTL ? 'ملخص النتائج' : 'Summary',
    totalChecks: isRTL ? 'إجمالي الفحوصات' : 'Total Checks',
    passed: isRTL ? 'ناجح' : 'Passed',
    withIssues: isRTL ? 'به مشاكل' : 'With Issues',
    critical: isRTL ? 'حرجة' : 'Critical',
    warning: isRTL ? 'تحذير' : 'Warning',
    info: isRTL ? 'معلومات' : 'Info',
    allPass: isRTL ? '🎉 ممتاز! كل الفحوصات ناجحة. لا توجد مشاكل في البيانات.' : '🎉 Excellent! All checks passed. Data integrity is clean.',
    code: isRTL ? 'الكود' : 'Code',
    entity: isRTL ? 'العنصر' : 'Entity',
    branch: isRTL ? 'الفرع' : 'Branch',
    description: isRTL ? 'الوصف' : 'Description',
    fix: isRTL ? 'الإصلاح المقترح' : 'Suggested Fix',
    issuesCount: isRTL ? 'عدد المشاكل' : 'Issues',
  };

  const runAudit = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await hrAuditService.run(fromDate, toDate);
      setReport(data);
    } catch (e: any) {
      setError(e.response?.data?.message || e.message || 'Audit failed');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    runAudit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isDark = theme.palette.mode === 'dark';
  // ✅ ألوان حساسة للوضع الليلي/النهاري
  const sevColor = (s: string) => s === 'Critical' ? (isDark ? '#FF5252' : '#D32F2F')
    : s === 'Warning' ? (isDark ? '#FFB74D' : '#F57C00')
    : (isDark ? '#4FC3F7' : '#0288D1');
  const sevBg = (s: string) => s === 'Critical' ? alpha(isDark ? '#FF5252' : '#D32F2F', isDark ? 0.15 : 0.08)
    : s === 'Warning' ? alpha(isDark ? '#FFB74D' : '#F57C00', isDark ? 0.15 : 0.08)
    : alpha(isDark ? '#4FC3F7' : '#0288D1', isDark ? 0.15 : 0.08);
  const passBg = alpha(isDark ? '#66BB6A' : '#388E3C', isDark ? 0.15 : 0.08);
  const passColor = isDark ? '#81C784' : '#388E3C';
  const textPrimary = theme.palette.text.primary;
  const textSecondary = theme.palette.text.secondary;
  const sevIcon = (s: string) => s === 'Critical' ? <CriticalIcon /> : s === 'Warning' ? <WarningIcon /> : <InfoIcon />;

  const categorized = report?.checks.reduce((acc: Record<string, HrAuditCheck[]>, c) => {
    const key = isRTL ? c.categoryAr : c.category;
    (acc[key] ||= []).push(c);
    return acc;
  }, {}) || {};

  return (
    <Box sx={{ p: 3, direction: isRTL ? 'rtl' : 'ltr' }}>
      {/* Header */}
      <Paper sx={{ p: 3, mb: 3, background: 'linear-gradient(135deg, #0D47A1 0%, #1976D2 50%, #42A5F5 100%)', color: '#fff' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
          <ReportIcon sx={{ fontSize: 40 }} />
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 'bold' }}>{t.title}</Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>{t.subtitle}</Typography>
          </Box>
        </Box>
      </Paper>

      {/* Controls */}
      <Paper sx={{ p: 2, mb: 3, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <TextField
          label={t.from}
          type="date"
          value={fromDate}
          onChange={e => setFromDate(e.target.value)}
          size="small"
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          label={t.to}
          type="date"
          value={toDate}
          onChange={e => setToDate(e.target.value)}
          size="small"
          InputLabelProps={{ shrink: true }}
        />
        <Button
          variant="contained"
          startIcon={<RefreshIcon />}
          onClick={runAudit}
          disabled={loading}
          sx={{ bgcolor: '#1976D2' }}
        >
          {loading ? t.running : t.runAudit}
        </Button>
        {report && (
          <Typography variant="caption" sx={{ ml: 'auto', color: 'text.secondary' }}>
            {t.generated}: {new Date(report.generatedAt).toLocaleString(isRTL ? 'ar-KW' : 'en-GB')}
          </Typography>
        )}
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading && !report && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 8 }}>
          <CircularProgress size={60} />
        </Box>
      )}

      {report && (
        <>
          {/* Summary Cards */}
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 2, mb: 3 }}>
            <Paper sx={{ p: 2, textAlign: 'center', borderTop: `4px solid ${isDark ? '#42A5F5' : '#1976D2'}` }}>
              <Typography variant="body2" sx={{ color: textSecondary }}>{t.totalChecks}</Typography>
              <Typography variant="h3" sx={{ color: isDark ? '#42A5F5' : '#1976D2', fontWeight: 'bold' }}>{report.totalChecks}</Typography>
            </Paper>
            <Paper sx={{ p: 2, textAlign: 'center', borderTop: `4px solid ${passColor}`, bgcolor: passBg }}>
              <Typography variant="body2" sx={{ color: passColor }}>✓ {t.passed}</Typography>
              <Typography variant="h3" sx={{ color: passColor, fontWeight: 'bold' }}>{report.checksPassed}</Typography>
            </Paper>
            <Paper sx={{ p: 2, textAlign: 'center', borderTop: `4px solid ${sevColor('Critical')}` }}>
              <Typography variant="body2" sx={{ color: textSecondary }}>✗ {t.withIssues}</Typography>
              <Typography variant="h3" sx={{ color: sevColor('Critical'), fontWeight: 'bold' }}>{report.checksWithIssues}</Typography>
            </Paper>
            <Paper sx={{ p: 2, textAlign: 'center', borderTop: `4px solid ${sevColor('Critical')}`, bgcolor: sevBg('Critical') }}>
              <Typography variant="body2" sx={{ color: sevColor('Critical') }}>🚨 {t.critical}</Typography>
              <Typography variant="h3" sx={{ color: sevColor('Critical'), fontWeight: 'bold' }}>{report.criticalCount}</Typography>
            </Paper>
            <Paper sx={{ p: 2, textAlign: 'center', borderTop: `4px solid ${sevColor('Warning')}`, bgcolor: sevBg('Warning') }}>
              <Typography variant="body2" sx={{ color: sevColor('Warning') }}>⚠ {t.warning}</Typography>
              <Typography variant="h3" sx={{ color: sevColor('Warning'), fontWeight: 'bold' }}>{report.warningCount}</Typography>
            </Paper>
            <Paper sx={{ p: 2, textAlign: 'center', borderTop: `4px solid ${sevColor('Info')}`, bgcolor: sevBg('Info') }}>
              <Typography variant="body2" sx={{ color: sevColor('Info') }}>ℹ {t.info}</Typography>
              <Typography variant="h3" sx={{ color: sevColor('Info'), fontWeight: 'bold' }}>{report.infoCount}</Typography>
            </Paper>
          </Box>

          {report.checksWithIssues === 0 && (
            <Alert severity="success" icon={<PassIcon />} sx={{ mb: 3, fontSize: '1.1rem', fontWeight: 'bold' }}>
              {t.allPass}
            </Alert>
          )}

          {/* Checks grouped by category */}
          {Object.entries(categorized).map(([category, checks]) => (
            <Paper key={category} sx={{ mb: 3, overflow: 'hidden' }}>
              <Box sx={{ p: 1.5, background: 'linear-gradient(135deg, #00796B, #00ACC1)', color: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>{category}</Typography>
                <Chip
                  label={`${checks.filter(c => c.issueCount === 0).length} / ${checks.length} ${t.passed}`}
                  size="small"
                  sx={{ bgcolor: 'rgba(255,255,255,0.25)', color: '#fff', fontWeight: 'bold' }}
                />
              </Box>

              {checks.map(check => (
                <Accordion key={check.code} disableGutters
                  sx={{
                    '&:before': { display: 'none' },
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    bgcolor: check.issueCount === 0 ? passBg : sevBg(check.severity),
                    color: textPrimary,
                  }}>
                  <AccordionSummary
                    expandIcon={check.issueCount > 0 ? <ExpandMoreIcon sx={{ color: textPrimary }} /> : null}
                    sx={{ '&.Mui-disabled': { opacity: 1 } }}
                    disabled={check.issueCount === 0}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                      <Box sx={{ color: check.issueCount === 0 ? passColor : sevColor(check.severity), display: 'flex' }}>
                        {check.issueCount === 0 ? <PassIcon /> : sevIcon(check.severity)}
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body1" sx={{ fontWeight: 'bold', color: textPrimary }}>
                          {isRTL ? check.titleAr : check.title}
                        </Typography>
                        <Typography variant="caption" sx={{ color: textSecondary, fontFamily: 'monospace' }}>
                          {check.code}
                        </Typography>
                      </Box>
                      {check.issueCount === 0 ? (
                        <Chip label="✓" size="small" sx={{ bgcolor: passColor, color: '#fff', fontWeight: 'bold' }} />
                      ) : (
                        <Chip
                          label={`${check.issueCount} ${t.issuesCount}`}
                          size="small"
                          sx={{ bgcolor: sevColor(check.severity), color: '#fff', fontWeight: 'bold' }}
                        />
                      )}
                    </Box>
                  </AccordionSummary>
                  {check.issueCount > 0 && (
                    <AccordionDetails sx={{ bgcolor: theme.palette.background.paper, p: 0 }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ bgcolor: alpha(sevColor(check.severity), isDark ? 0.2 : 0.1) }}>
                            <TableCell sx={{ fontWeight: 'bold', width: 110, color: textPrimary }}>{t.code}</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', color: textPrimary }}>{t.entity}</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', width: 140, color: textPrimary }}>{t.branch}</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', color: textPrimary }}>{t.description}</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', color: textPrimary }}>{t.fix}</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {check.issues.map((iss, idx) => (
                            <TableRow key={idx} hover>
                              <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.85rem', color: isDark ? '#64B5F6' : '#1976D2' }}>
                                {iss.entityCode || `#${iss.entityId}`}
                              </TableCell>
                              <TableCell sx={{ color: textPrimary }}>{iss.entityName || '—'}</TableCell>
                              <TableCell sx={{ color: textPrimary }}>{iss.branchName || '—'}</TableCell>
                              <TableCell sx={{ color: sevColor(check.severity), fontWeight: 500 }}>
                                {isRTL ? iss.descriptionAr : iss.description}
                              </TableCell>
                              <TableCell sx={{ color: passColor, fontSize: '0.85rem' }}>
                                💡 {isRTL ? iss.suggestedFixAr : iss.suggestedFix}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </AccordionDetails>
                  )}
                </Accordion>
              ))}
            </Paper>
          ))}
        </>
      )}
    </Box>
  );
};

export default HrAuditReportPage;
